import React from 'react'
import styled from 'styled-components'

// Elements
import ParseContent from 'components/shared/ParseContent'

// Components
import Image from 'components/shared/ImageGatsby'

// SVG
import ButtonDefault from 'components/elements/ButtonDefault'

const StyledImage = styled(Image)`
  @media (min-width: 992px) {
    height: 600px;
  }
  @media (max-width: 991px) {
    height: 350px;
  }
`

const Wrapper = styled.section`
  overflow: visible;

  @media (min-width: 992px) {
    margin-bottom: 10rem;
    /* Room for ColumnDiv hanging below the image */
    padding-bottom: clamp(3rem, 8vw, 6rem);
  }
`

const ColumnDiv = styled.div`
  position: absolute;
  z-index: 2;
  /* 65px from viewport left; breakout from column */
  left: calc(100% - 100vw + 65px);
  /* Extend below the banner/image bottom (overlap section) */
  bottom: clamp(-5.5rem, -8vw, -3rem);
  top: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.25rem;
  min-width: min(725px, 92vw);
  min-height: 300px;
  max-width: 725px;
  padding: clamp(2rem, 4vw, 3.75rem);
  padding-left: max(clamp(2rem, 4vw, 3.75rem), env(safe-area-inset-left, 0px));
  background-color: ${(props) => props.theme.color.face.custom};
  border-radius: 75px 0 0 0;

  @media (min-width: 2000px) {
    /* Row switches to justify-content-center */
    left: calc((100% - 100vw) / 2 + 65px);
  }

  @media (max-width: 991px) {
    position: relative;
    top: 0;
    bottom: auto;
    left: 0;
    min-width: 213px;
    max-width: 100%;
    border-radius: 0;
    box-shadow: none;
    padding-left: clamp(2rem, 4vw, 3.75rem);
  }

  @media (max-width: 575px) {
    max-width: 100%;
  }

  & h1 {
    margin: 0;
    font-family: ${(props) => props.theme.font.family.secondary};
    font-size: ${(props) => props.theme.font.size['30']};
    line-height: 1.15;
    font-weight: ${(props) => props.theme.font.weight.l};
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: ${(props) => props.theme.color.text.contrast};
  }
`

const Content = styled(ParseContent)`
  flex: 1 1 auto;
  font-family: ${(props) => props.theme.font.family.main};
  font-size: ${(props) => props.theme.font.size.base};
  font-weight: ${(props) => props.theme.font.weight.s};
  line-height: 1.55;
  color: ${(props) => props.theme.color.text.main};

  & p {
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  & p + ul {
    margin-top: 0.5rem;
  }

  & ul {
    margin: 0 0 0.5rem;
    padding-left: 1.15rem;
    list-style: disc;
  }

  & li {
    margin-bottom: 0.35rem;
    padding-left: 0.15rem;

    &::marker {
      color: ${(props) => props.theme.color.text.main};
    }
  }

  & img {
    width: 60px;
    height: 60px;
    opacity: 0.2;
    @media (max-width: 991px) {
      max-width: 40px;
      max-height: 40px;
    }
  }

  & h1,
  & h2,
  & h3,
  & h4,
  & h5 {
    font-family: ${(props) => props.theme.font.family.secondary};
    font-size: ${(props) => props.theme.font.size['21']};
    color: ${(props) => props.theme.color.text.lightDark};
    line-height: 1.25;
  }
`

const Row = styled.div`
  @media (min-width: 2000px) {
    justify-content: center !important;
  }
`

const Column = styled.div`
  max-width: 1120px;
  overflow: visible;

  @media (min-width: 991px) {
    min-height: 600px;
  }
`

interface BannerRightProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPost_Flexcontent_Flex_Banner
}

function BannerRight({ fields }: BannerRightProps) {
  return (
    <Wrapper className="mt-4 pb-4 pb-lg-0">
      <div className="container-fluid p-0">
        <Row className="row justify-content-end">
          <Column className="col-lg-8 col-12 position-relative">
            <div className="d-flex justify-content-center">
              <StyledImage
                image={fields.image}
                className="position-relative position-lg-absolute"
                alt="Lofthus"
                loading="lazy"
              />
            </div>

            <ColumnDiv>
              <h1>{fields?.title}</h1>
              {fields?.description && <Content content={fields.description} />}
              {fields?.links?.[0]?.link && (
                <ButtonDefault
                  to={fields?.links?.[0]?.link?.url || ''}
                  className="d-inline-flex"
                >
                  {fields?.links?.[0]?.link?.title}
                </ButtonDefault>
              )}
            </ColumnDiv>
          </Column>
        </Row>
      </div>
    </Wrapper>
  )
}

export default BannerRight
